import * as React from "react";

import { defineMessages, FormattedMessage } from "react-intl";

import AkButton from "@atlaskit/button/loading-button";
import { Box, xcss } from "@atlaskit/primitives";
import { token as akToken } from "@atlaskit/tokens";

import { orgBasedOfferingIdMap } from "@/components/signup/utils/products";

import { Card } from "../ui/card/card";
import { Centered } from "../ui/centered/centered";
import { OrgIcon } from "../ui/org-icon/org-icon";

import { Organization } from "./organizations";

const boxStyles = xcss({
    fontSize: akToken("font.body", "14px"),
    color: "color.text",
    fontWeight: "400"
});

const secondaryDetailsStyles = xcss({
    fontSize: akToken("font.body.small", "11px"),
    color: "color.text.subtlest",
    fontWeight: "400"
});

const enabledStyles = xcss({
    display: "flex",
    color: "color.text.subtle",
    fontSize: akToken("font.body.small", "11px")
});

interface OrganizationCardProps {
    id: string;
    organization: Organization;
    selectHandler(arg: string): void;
    isSubmitting: boolean;
    offeringIds?: string[];
}
export const OrganizationCard = ({
    id,
    organization,
    selectHandler,
    isSubmitting,
    offeringIds
}: OrganizationCardProps) => {
    const isOrgContainsEntitlement =
        organization.entitlementsByOfferingIds &&
        organization.entitlementsByOfferingIds.some((entitlements) => {
            // If product on an org is a collection, always allow provisioning
            const orgBasedProduct =
                orgBasedOfferingIdMap[entitlements.offeringId];
            const isEntitlementCollection =
                orgBasedProduct &&
                (orgBasedProduct.startsWith("TWC") ||
                    orgBasedProduct.startsWith("STRATEGY") ||
                    orgBasedProduct.startsWith("SERVICE_COLLECTION"));

            return (
                offeringIds &&
                offeringIds.includes(entitlements.offeringId) &&
                !isEntitlementCollection &&
                entitlements.entitlementIds &&
                entitlements.entitlementIds.length > 0
            );
        });
    const provisionEnabled = !isOrgContainsEntitlement;

    const checkOfferingIdProduct = (product: string) =>
        offeringIds &&
        offeringIds.some(
            (offeringId) =>
                orgBasedOfferingIdMap[offeringId] &&
                orgBasedOfferingIdMap[offeringId].startsWith(product)
        );

    const isCreditPackOffering = () =>
        offeringIds &&
        offeringIds.some((offeringId) => {
            const orgBasedProduct = orgBasedOfferingIdMap[offeringId];
            return (
                orgBasedProduct?.endsWith("_CREDITS_PACK") &&
                orgBasedProduct !== "ROVO_CREDITS_PACK"
            );
        });

    const getCardMsg = () => {
        switch (true) {
            case checkOfferingIdProduct("GUARD"):
                return OrganizationCard.messages.alreadyEnabledGuard;
            case checkOfferingIdProduct("ROVO_CREDITS_PACK"):
                return OrganizationCard.messages.alreadyEnabledRovoCreditsPack;
            case isCreditPackOffering():
                return OrganizationCard.messages.alreadyEnabledCreditPack;
            case checkOfferingIdProduct("ROVO"):
                return OrganizationCard.messages.alreadyEnabledRovo;
            case checkOfferingIdProduct("CUSTOMER_MANAGED_KEYS"):
                return OrganizationCard.messages
                    .alreadyEnabledCustomerManagedKeys;
            case checkOfferingIdProduct("SERVICE_COLLECTION"):
                return OrganizationCard.messages
                    .alreadyEnabledServiceCollection;
            case checkOfferingIdProduct("INDEXED_OBJECTS_PACK"):
                return OrganizationCard.messages
                    .alreadyEnabledIndexedObjectsPack;
            default:
                return OrganizationCard.messages.alreadyEnabledRovo; // default to rovoSubtitle if no match
        }
    };

    const cardMsg = getCardMsg();

    const buttonClickHandler = () => {
        selectHandler(id);
    };

    const linkClickHandler = () => {
        if (!provisionEnabled) return;
        selectHandler(id);
    };

    return (
        <React.Fragment>
            <Card
                mainContent={
                    <>
                        <Box as="span" xcss={boxStyles}>
                            {organization.displayName}
                        </Box>
                        <Box xcss={secondaryDetailsStyles}>Organization</Box>
                    </>
                }
                icon={
                    <OrgIcon
                        primaryColor={akToken("color.icon.inverse", "#FFFFFF")}
                    />
                }
                actions={
                    provisionEnabled ? (
                        <Centered>
                            <Box>
                                <AkButton
                                    onClick={buttonClickHandler}
                                    isDisabled={isSubmitting}
                                >
                                    <FormattedMessage
                                        {...OrganizationCard.messages.select}
                                    />
                                </AkButton>
                            </Box>
                        </Centered>
                    ) : (
                        <Box xcss={enabledStyles}>
                            <Centered>
                                <FormattedMessage {...cardMsg} />
                            </Centered>
                        </Box>
                    )
                }
                detailsSeparator="space"
                {...(provisionEnabled && { href: "#" })}
                onClick={linkClickHandler}
                color="light"
                testId={id}
            />
        </React.Fragment>
    );
};

OrganizationCard.messages = defineMessages({
    select: {
        id: "Wac.Signup.PostOrder.Rovo.organization.card.select",
        defaultMessage: "Select",
        description: "Button text to select an org on Rovo landing page"
    },
    alreadyEnabledRovo: {
        id: "Wac.Signup.PostOrder.Rovo.organization.card.alreadyEnabledRovo",
        defaultMessage: "This organization already has Rovo",
        description: "Description for orgs with Rovo on landing page"
    },
    alreadyEnabledGuard: {
        id: "Wac.Signup.PostOrder.Guard.organization.card.alreadyEnabledGuard",
        defaultMessage: "This organization already has Guard",
        description: "Description for orgs with Guard on landing page"
    },
    alreadyEnabledStrategy: {
        id: "Wac.Signup.PostOrder.Strategy.organization.card.alreadyEnabledStrategy",
        defaultMessage: "This organization already has Strategy",
        description: "Description for orgs with Strategy on landing page"
    },
    alreadyEnabledTwc: {
        id: "Wac.Signup.PostOrder.Twc.organization.card.alreadyEnabledTwc",
        defaultMessage: "This organization already has Twc",
        description: "Description for orgs with Twc on landing page"
    },
    alreadyEnabledCustomerManagedKeys: {
        id: "Wac.Signup.PostOrder.CustomerManagedKeys.organization.card.alreadyEnabledCustomerManagedKeys",
        defaultMessage: "This organization already has Customer Managed Keys",
        description:
            "Description for orgs with Customer Managed Keys on landing page"
    },
    alreadyEnabledServiceCollection: {
        id: "Wac.Signup.PostOrder.ServiceCollection.organization.card.alreadyEnabledServiceCollection",
        defaultMessage: "This organization already has Service Collection",
        description:
            "Description for orgs with Service Collection on landing page"
    },
    alreadyEnabledRovoCreditsPack: {
        id: "Wac.Signup.PostOrder.RovoCreditsPack.organization.card.alreadyEnabledRovoCreditsPack",
        defaultMessage: "This organization already has Rovo Credits Pack",
        description:
            "Description for organizations with Rovo Credits Pack on the post-order organization setup page"
    },
    alreadyEnabledCreditPack: {
        id: "Wac.Signup.PostOrder.CreditPack.organization.card.alreadyEnabledCreditPack",
        defaultMessage: "This organization already has this credit pack",
        description:
            "Message shown on a disabled organization card when the selected organization already has the credit pack from the user's post-order purchase"
    },
    alreadyEnabledIndexedObjectsPack: {
        id: "Wac.Signup.PostOrder.IndexedObjectsPack.organization.card.alreadyEnabledIndexedObjectsPack",
        defaultMessage: "This organization already has Indexed Objects Pack",
        description:
            "Description for orgs with Indexed Objects Pack on landing page"
    }
});
